// lens viewer SPA + minimal operator control.
//
// Two roles in one tiny page (v1 — deliberately minimal):
//
//  • Viewer — given a device token, polls GET /api/v1/live/:token and plays the
//    returned LL-HLS manifest in a <video>. For NON-PUBLIC streams the manifest
//    is a SIGNED short-TTL URL (§6); the viewer RE-MINTS it by re-calling
//    live/:token at ~50% TTL (60s) while playing, so a leaked URL dies fast.
//    Playback uses hls.js where MSE is needed; Safari plays HLS natively, so we
//    feature-detect and skip hls.js there.
//
//  • Operator — holds the device's ed25519 key (generated/stored in-browser,
//    exactly like Locus) and can register the device with lens + sign live/start
//    / live/stop. The signed bytes are produced by src/sign.ts, byte-identical
//    to what the Phase 2 server verifies. The ingest URL + stream key returned by
//    live/start go to the operator ONLY (the publish credential never rides a
//    read path) and are shown so the operator can point ffmpeg/OBS/Larix at them.
//
// This file is the SPA shell + wiring only; the signing contract lives in
// src/sign.ts and the playback authorization in the server.

import Hls from 'hls.js';
import { generateKeypair, signLiveWrite, type StoredKeypair } from './sign';

// ── persistence (operator key + identity) ───────────────────────────────────
// The operator's keypair + token live in localStorage, mirroring Locus's
// in-browser key custody. A viewer-only visitor never generates a key.
const KP_KEY = 'lens.operator.keypair';
const TOKEN_KEY = 'lens.operator.token';

function loadKeypair(): StoredKeypair | null {
  const raw = localStorage.getItem(KP_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as StoredKeypair;
    return parsed.priv && parsed.pub ? parsed : null;
  } catch {
    return null;
  }
}

function saveKeypair(kp: StoredKeypair): void {
  localStorage.setItem(KP_KEY, JSON.stringify(kp));
}

// ── DOM shell ────────────────────────────────────────────────────────────────
const root = document.getElementById('app');
if (!root) throw new Error('#app element missing');

root.innerHTML = `
  <header class="topbar">
    <div class="brand">
      <span class="logo"><i class="ph ph-broadcast"></i></span>
      <span class="wordmark">lens</span>
      <span class="tag">live · recordings</span>
    </div>
  </header>

  <div class="wrap" id="viewer-view">
    <section class="card viewer-card">
      <div class="card-body">
        <div class="video-shell" id="video-shell">
          <div class="video-rotate-wrap" id="video-rotate-wrap">
            <video id="video" playsinline muted></video>
          </div>
        </div>
        <div class="video-bar">
          <button id="play-btn" class="vbar-btn" type="button" title="Play / pause"><i class="ph ph-play"></i></button>
          <button id="mute-btn" class="vbar-btn" type="button" title="Mute / unmute"><i class="ph ph-speaker-slash"></i></button>
          <button id="fullscreen-btn" class="vbar-btn" type="button" title="Fullscreen"><i class="ph ph-arrows-out"></i></button>
          <button id="rotate-btn" class="vbar-btn" type="button" title="Rotate 90°"><i class="ph ph-arrows-clockwise"></i> Rotate</button>
          <button id="live-btn" class="vbar-btn vbar-live" type="button" title="Jump to live edge" hidden><i class="ph ph-fast-forward"></i> Live</button>
          <span id="video-time" class="vbar-time">–</span>
        </div>
        <p id="watch-status" class="status"><span class="status-dot"></span><span class="status-text">Enter a device token to watch its live stream + recordings.</span></p>
        <div class="identify">
          <div class="field">
            <label class="field-label" for="watch-token">Device token</label>
            <input id="watch-token" type="text" placeholder="trail-…" />
          </div>
          <div class="field-row">
            <div class="field">
              <label class="field-label" for="watch-group">Group prefix</label>
              <input id="watch-group" type="text" placeholder="team-alpha" />
            </div>
            <div class="field">
              <label class="field-label" for="watch-share">Share handle</label>
              <input id="watch-share" type="text" placeholder="optional" />
            </div>
          </div>
          <button id="watch" class="btn btn--primary btn--block" type="button"><i class="ph ph-play"></i> Watch</button>
        </div>
      </div>
    </section>

    <section class="card rec-section">
      <div class="card-body">
        <div class="section-head">
          <h2 class="set-title">Recordings</h2>
          <span class="set-desc">last 30 days</span>
        </div>
        <div id="rec-grid" class="rec-grid"></div>
        <p id="rec-empty" class="set-desc rec-empty">Identify a device above to see its recordings.</p>
      </div>
    </section>

  </div>

  <div class="wrap operator-view" id="operator-view" hidden>
    <button id="op-back" class="btn btn--quiet" type="button"><i class="ph ph-arrow-left"></i> Back to viewer</button>
    <section class="card operator">
      <div class="card-body">
        <div class="section-head">
          <h2 class="set-title">Operator control</h2>
          <span class="set-desc">register · go live · publish</span>
        </div>
        <p class="set-desc op-note">Device-side tooling: mint a key, register the device, and read back the RTMPS ingest URL for OBS/ffmpeg. Most devices provision over the API instead.</p>
        <div class="operator-body">
          <p id="op-identity" class="op-identity">no operator key yet</p>
        <button id="op-init" class="btn btn--ghost" type="button"><i class="ph ph-key"></i> Generate / load operator key</button>
        <div class="field">
          <label class="field-label" for="op-token">Device token</label>
          <input id="op-token" type="text" placeholder="trail-…" />
        </div>
        <div class="field-row">
          <div class="field">
            <label class="field-label" for="op-visibility">Visibility</label>
            <select id="op-visibility">
              <option value="unlisted">unlisted</option>
              <option value="public">public</option>
              <option value="group">group</option>
            </select>
          </div>
          <div class="field">
            <label class="field-label" for="op-group">Group prefix</label>
            <input id="op-group" type="text" placeholder="team-alpha" />
          </div>
        </div>
        <div class="field">
          <label class="field-label" for="op-cohort">Cohort id (optional · Locus self-echo)</label>
          <input id="op-cohort" type="text" placeholder="opaque id" />
        </div>
        <div class="op-actions">
          <button id="op-register" class="btn btn--ghost" type="button"><i class="ph ph-plus-circle"></i> Register device</button>
          <button id="op-start" class="btn btn--primary" type="button"><i class="ph ph-broadcast"></i> Go live</button>
          <button id="op-stop" class="btn btn--quiet" type="button"><i class="ph ph-stop"></i> Stop</button>
        </div>
          <pre id="op-out" class="op-out"></pre>
        </div>
      </div>
    </section>
  </div>
`;

function $(id: string): HTMLElement {
  const el = document.getElementById(id);
  if (!el) throw new Error(`#${id} missing`);
  return el;
}

// ── viewer ───────────────────────────────────────────────────────────────────
let hls: Hls | null = null;

// Tear down playback on a watch-switch or operator-nav. Signed playback tokens
// are session-scale (ADR 0007), so there is no refresh timer to cancel — only
// the hls instance to destroy.
function stopPlayback(): void {
  if (hls) {
    hls.destroy();
    hls = null;
  }
}

// Build the live/:token query carrying whatever proof the viewer presented
// (group prefix / share handle) so a non-public stream can authorize.
function liveTokenUrl(token: string, group: string, share: string): string {
  const u = new URL(`/api/v1/live/${encodeURIComponent(token)}`, window.location.origin);
  if (group) u.searchParams.set('group', group);
  if (share) u.searchParams.set('share', share);
  return u.toString();
}

// Attach a manifest URL to the <video>. Safari plays HLS natively (feature-
// detect via canPlayType) and skips hls.js; everyone else uses hls.js + MSE.
function attachManifest(video: HTMLVideoElement, manifest: string): void {
  const canNative = video.canPlayType('application/vnd.apple.mpegurl') !== '';
  if (canNative && !Hls.isSupported()) {
    video.src = manifest;
    void video.play().catch(() => {/* autoplay may be blocked; controls remain */});
    return;
  }
  if (Hls.isSupported()) {
    // Recreate hls per manifest so swapping to a different watch/recording
    // cleanly replaces the source.
    if (hls) hls.destroy();
    // Measured against the real stream: Cloudflare serves a STANDARD (non-LL) HLS
    // manifest, ~2 s segments, an 8-segment / ~16 s sliding window that advances at
    // realtime, plus an ABR ladder (720/480/360/240p). The source is healthy, so
    // the player just needs to sit calmly in the middle of the window and let
    // HLS.js do its own live management — DO NOT add manual stall recovery.
    //
    // A prior `waiting → setTimeout(3s) → hls.startLoad()` "stall recovery" was the
    // bug behind "restarts every ~4 s and replays old segments": on a live stream
    // startLoad() re-seeks to the sync position, every transient `waiting` re-armed
    // the 3 s timer, and the resync jumped the playhead back — a ~4 s restart loop.
    // It is gone. lowLatencyMode:false honours liveSyncDurationCount; sitting ~4
    // segments (~8 s) back centres the playhead — runway toward the live edge AND
    // margin before the stale edge — so normal jitter never reaches either.
    hls = new Hls({
      lowLatencyMode: false,
      liveSyncDurationCount: 4, // ~8 s behind edge — centre of the ~16 s window
      maxBufferLength: 16,      // can't buffer past the ~16 s window anyway
    });
    hls.loadSource(manifest);
    hls.attachMedia(video);
    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      void video.play().catch(() => {/* autoplay may be blocked */});
    });
    // Follow a SOURCE RESTART. When the phone starts a new live session (a stop→go
    // live, or some RTMP reconnects) Cloudflare publishes a brand-new rendition
    // under the SAME input manifest. HLS.js loaded the master once and only polls
    // the OLD rendition's media playlist — which now freezes, so the viewer ends up
    // replaying the last seconds of the previous session instead of the live feed.
    // Detect the frozen LIVE playlist (endSN not advancing for >15 s while it still
    // claims to be live) and reload the source: loadSource re-fetches the master and
    // attaches to the CURRENT session's live edge — a forward catch-up, never a
    // backward replay. VOD playlists report live=false and are left alone.
    let lastEndSN = -1;
    let lastAdvanceMs = Date.now();
    hls.on(Hls.Events.LEVEL_UPDATED, (_, data) => {
      const d = data.details;
      if (!d || !d.live) return;
      if (d.endSN !== lastEndSN) {
        lastEndSN = d.endSN;
        lastAdvanceMs = Date.now();
        return;
      }
      if (Date.now() - lastAdvanceMs > 15000) {
        lastAdvanceMs = Date.now(); // throttle: at most one reload per 15 s window
        lastEndSN = -1;
        hls!.loadSource(manifest); // re-fetch master → jump to the new live edge
      }
    });
    // Recover only FATAL errors, and only the non-disruptive way: retry loading on
    // a network error, reset the decode pipeline on a media error. No seeking.
    hls.on(Hls.Events.ERROR, (_, data) => {
      if (!data.fatal) return;
      if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
        hls!.startLoad();
      } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
        hls!.recoverMediaError();
      }
    });
    return;
  }
  // Last resort: native even if canPlayType was empty (older Safari quirk).
  video.src = manifest;
}

// Set the viewer status line + its state (a pulsing dot when live). Keeps the
// dot span intact (we only swap the text + the parent's state class).
type StatusKind = 'idle' | 'live' | 'error';
function setStatus(text: string, kind: StatusKind = 'idle'): void {
  const el = $('watch-status');
  el.className = 'status' + (kind === 'live' ? ' is-live' : kind === 'error' ? ' is-error' : '');
  const t = el.querySelector('.status-text');
  if (t) t.textContent = text;
  else el.textContent = text;
  // The "Live" jump-to-edge button only makes sense while watching a live stream
  // (a VOD recording has no live edge); show it only in the 'live' state.
  const liveBtn = document.getElementById('live-btn');
  if (liveBtn) liveBtn.hidden = kind !== 'live';
}

// ── recordings (VOD) gallery ─────────────────────────────────────────────────
// After identifying a device, list its past 30-day Stream recordings (gated
// identically to live — see functions/api/v1/live/[token]/recordings.ts) and
// render playable cards. A `ready` card plays into the SAME <video> as live.
interface RecOut {
  uid: string;
  created: string;
  duration: number;
  status: 'ready' | 'in-progress';
  thumbnail: string;
  playback: string;
}

// Guards against a stale response: a newer identify bumps the id, so an older
// in-flight fetch that resolves later does not overwrite the current grid.
let recordingsReqId = 0;

function escAttr(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function escHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function fmtDuration(sec: number): string {
  if (!sec || sec <= 0) return '';
  const s = Math.round(sec);
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m >= 60) {
    const h = Math.floor(m / 60);
    return `${h}:${String(m % 60).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
  }
  return `${m}:${String(r).padStart(2, '0')}`;
}
function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function recordingsUrl(token: string, group: string, share: string): string {
  const u = new URL(`/api/v1/live/${encodeURIComponent(token)}/recordings`, window.location.origin);
  if (group) u.searchParams.set('group', group);
  if (share) u.searchParams.set('share', share);
  return u.toString();
}

function showRecHint(hint: string): void {
  ($('rec-grid') as HTMLElement).innerHTML = '';
  const empty = $('rec-empty');
  empty.textContent = hint;
  empty.hidden = false;
}

function playRecording(rec: RecOut): void {
  stopPlayback();
  const video = $('video') as HTMLVideoElement;
  setStatus(`recording · ${fmtDate(rec.created)}`, 'idle');
  attachManifest(video, rec.playback);
  video.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function renderRecordings(list: RecOut[]): void {
  const grid = $('rec-grid');
  const empty = $('rec-empty');
  if (!list.length) {
    grid.innerHTML = '';
    empty.textContent = 'No recordings in the last 30 days.';
    empty.hidden = false;
    return;
  }
  empty.hidden = true;
  grid.innerHTML = list
    .map((r, i) => {
      const ready = r.status === 'ready';
      const dur = fmtDuration(r.duration);
      const pill = ready
        ? '<span class="rec-pill is-ready">ready</span>'
        : '<span class="rec-pill is-live">recording</span>';
      const thumb = ready
        ? `<img loading="lazy" src="${escAttr(r.thumbnail)}" alt="" />`
        : '<i class="ph ph-record-fill"></i>';
      return `<button class="rec-card" type="button" data-i="${i}"${ready ? '' : ' disabled'}>
        <div class="rec-thumb">${thumb}${dur ? `<span class="rec-dur">${escHtml(dur)}</span>` : ''}</div>
        <div class="rec-meta"><span class="rec-date">${escHtml(fmtDate(r.created))}</span>${pill}</div>
      </button>`;
    })
    .join('');
  grid.querySelectorAll('.rec-card').forEach((el) => {
    el.addEventListener('click', () => {
      const i = Number((el as HTMLElement).dataset.i);
      const rec = list[i];
      if (rec && rec.status === 'ready') playRecording(rec);
    });
  });
}

// Fetch + render a device's recordings. Called on every successful identify (so
// recordings show even when the device is currently OFFLINE — past sessions
// outlive the live stream). The reqId guard drops a superseded response.
async function loadRecordings(token: string, group: string, share: string): Promise<void> {
  const reqId = ++recordingsReqId;
  showRecHint('Loading recordings…');
  let res: Response;
  try {
    res = await fetch(recordingsUrl(token, group, share), { headers: { accept: 'application/json' } });
  } catch {
    if (reqId === recordingsReqId) showRecHint('Could not load recordings.');
    return;
  }
  if (reqId !== recordingsReqId) return; // a newer identify superseded this one
  const body = (await res.json().catch(() => null)) as { recordings?: RecOut[] } | null;
  if (reqId !== recordingsReqId) return;
  if (!res.ok || !body || !Array.isArray(body.recordings)) {
    showRecHint('Could not load recordings.');
    return;
  }
  renderRecordings(body.recordings);
}

// Clear the grid for a context with no device token (e.g. the #/w deep-link,
// which carries only the opaque cohort id). Bumps reqId so any in-flight load
// can't repaint it.
function clearRecordings(hint: string): void {
  recordingsReqId += 1;
  showRecHint(hint);
}

async function watch(): Promise<void> {
  stopPlayback();
  const token = ($('watch-token') as HTMLInputElement).value.trim();
  const group = ($('watch-group') as HTMLInputElement).value.trim();
  const share = ($('watch-share') as HTMLInputElement).value.trim();
  const video = $('video') as HTMLVideoElement;
  if (!token) {
    setStatus('Enter a device token.', 'error');
    return;
  }

  // Recordings load on identify regardless of live status — a device that is
  // offline now may still have past sessions to watch.
  void loadRecordings(token, group, share);

  setStatus('resolving…');
  let res: Response;
  try {
    res = await fetch(liveTokenUrl(token, group, share));
  } catch (err) {
    setStatus(`error: ${(err as Error).message}`, 'error');
    return;
  }
  const body = (await res.json().catch(() => null)) as
    | { status?: string; playback?: string; signed?: boolean; ttl_seconds?: number }
    | null;
  if (!res.ok || !body) {
    setStatus(`error: ${res.status}`, 'error');
    return;
  }
  if (body.status !== 'live' || !body.playback) {
    setStatus('Offline (or not authorized) — see recordings below.', 'idle');
    return;
  }

  setStatus(body.signed ? 'Live · signed (expiring URL)' : 'Live · public', 'live');
  attachManifest(video, body.playback);
  // The signed token is session-scale (ADR 0007), so resolve+attach once — no
  // re-mint loop. stopPlayback() tears down hls on the next watch / nav.
}

$('watch').addEventListener('click', () => void watch());

// ── Video control bar ─────────────────────────────────────────────────────────
// The video has no native `controls` so the bar never rotates with the feed.
const vid = document.getElementById('video') as HTMLVideoElement;

const playBtn = $('play-btn');
const muteBtn = $('mute-btn');
// Sync play/pause icon with video state (autoplay + HLS attach both fire events).
vid.addEventListener('play',  () => { playBtn.innerHTML = '<i class="ph ph-pause"></i>'; });
vid.addEventListener('pause', () => { playBtn.innerHTML = '<i class="ph ph-play"></i>'; });
vid.addEventListener('volumechange', () => {
  muteBtn.innerHTML = vid.muted
    ? '<i class="ph ph-speaker-slash"></i>'
    : '<i class="ph ph-speaker-high"></i>';
});
// Video starts muted for autoplay; show the muted icon initially.
muteBtn.innerHTML = '<i class="ph ph-speaker-slash"></i>';

// Time display — formats currentTime as H:MM:SS or M:SS.
const videoTime = $('video-time');
function fmtVideoTime(sec: number): string {
  const s = Math.floor(sec);
  const m = Math.floor(s / 60) % 60;
  const h = Math.floor(s / 3600);
  const ss = String(s % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}
vid.addEventListener('timeupdate', () => {
  videoTime.textContent = vid.currentTime > 0 ? fmtVideoTime(vid.currentTime) : '–';
});

playBtn.addEventListener('click', () => { void (vid.paused ? vid.play() : Promise.resolve(vid.pause())); });
muteBtn.addEventListener('click', () => { vid.muted = !vid.muted; });
$('fullscreen-btn').addEventListener('click', () => {
  if (!document.fullscreenElement) {
    void document.getElementById('video-shell')!.requestFullscreen();
  } else {
    void document.exitFullscreen();
  }
});

// Jump to the live edge. After drifting back (a pause, a tab in the background,
// or just watching behind the edge) this catches the player up to the freshest
// segment without restarting the stream — a restart would spin up a brand-new
// Cloudflare muxing session and drop every viewer. Prefer HLS.js's own
// liveSyncPosition (a hair behind the absolute edge, for stability); fall back to
// the end of the seekable range for native (Safari) playback.
$('live-btn').addEventListener('click', () => {
  const sync = hls?.liveSyncPosition;
  if (typeof sync === 'number' && isFinite(sync)) {
    vid.currentTime = sync;
  } else if (vid.seekable.length > 0) {
    vid.currentTime = vid.seekable.end(vid.seekable.length - 1);
  }
  void vid.play().catch(() => {});
});

// Rotation: cycles 0 → 90 → 180 → 270 → 0. Applied to the inner
// .video-rotate-wrap so the control bar below stays upright.
const ROTATIONS = [0, 90, 180, 270] as const;
let rotationIdx = 0;
$('rotate-btn').addEventListener('click', () => {
  rotationIdx = (rotationIdx + 1) % ROTATIONS.length;
  document.getElementById('video-shell')!.dataset.rotation = String(ROTATIONS[rotationIdx]);
});

// "Back to viewer" from the unlisted operator console — drop the hash and re-route.
$('op-back').addEventListener('click', () => {
  history.replaceState(null, '', window.location.pathname + window.location.search);
  route();
});

// ── deep-link watch route (#/w?g=<group>&c=<cohortId>) ────────────────────────
// A badge-friendly entry point: Locus cohort live-badges link here instead of
// the raw signed manifest (which browsers download and which dies with the
// signed-URL TTL). The badge holds only the group prefix + the device's OPAQUE
// cohort id (the raw token is hidden), so we play by POLLING discovery
// (GET /api/v1/live?group=<group>), finding the device whose `token === cohortId`,
// and attaching its `playback`. One discovery resolve, no re-poll — the token is
// session-scale (ADR 0007). Query params (not a path) so the group's hyphen and
// the base64url cohort id (which can contain -/_) stay unambiguous. Purely
// additive: the form-based watch() above is untouched.
async function watchViaDiscovery(group: string, cohortId: string): Promise<void> {
  stopPlayback();
  // A watch link carries only the opaque cohort id, not the raw device token the
  // recordings endpoint keys on — so the gallery isn't available on this path.
  clearRecordings('Recordings need the device token (open this device in the form).');
  const video = $('video') as HTMLVideoElement;
  if (!group || !cohortId) {
    setStatus('Invalid watch link.', 'error');
    return;
  }
  setStatus('resolving…');
  let res: Response;
  try {
    res = await fetch(`/api/v1/live?group=${encodeURIComponent(group)}`, {
      headers: { accept: 'application/json' },
    });
  } catch (err) {
    setStatus(`error: ${(err as Error).message}`, 'error');
    return;
  }
  const body = (await res.json().catch(() => null)) as
    | { devices?: Array<{ token?: string; status?: string; playback?: string; signed?: boolean; ttl_seconds?: number }> }
    | null;
  if (!res.ok || !body) {
    setStatus(`error: ${res.status}`, 'error');
    return;
  }
  const dev = Array.isArray(body.devices)
    ? body.devices.find((d) => d?.token === cohortId)
    : undefined;
  if (!dev || dev.status !== 'live' || !dev.playback) {
    setStatus('Offline (or no longer live in this group).', 'idle');
    return;
  }
  setStatus(dev.signed ? 'Live · signed (expiring URL)' : 'Live · public', 'live');
  attachManifest(video, dev.playback);
  // The signed token is session-scale (ADR 0007): resolve discovery once and
  // attach — no re-poll loop, so no recurring load on the per-IP discovery budget.
}

// Hash routes:
//   #/operator         → the UNLISTED operator console (device-side publishing
//                        tooling). There is no link to it from the viewer —
//                        operators navigate to it directly, mirroring Locus's
//                        #/admin (ADR 0021). It is a client convenience only; the
//                        real trust boundary is the server's ed25519 verification.
//   #/w?g=&c=          → deep-link watch (the Locus badge target) in the viewer.
//   anything else/none → the viewer.
function showOperatorView(on: boolean): void {
  ($('viewer-view') as HTMLElement).hidden = on;
  ($('operator-view') as HTMLElement).hidden = !on;
  if (on) stopPlayback(); // don't keep the (now hidden) viewer's stream running
}

function route(): void {
  const hash = window.location.hash;
  if (hash.startsWith('#/operator')) {
    showOperatorView(true);
    return;
  }
  showOperatorView(false);
  if (hash.startsWith('#/w?')) {
    const params = new URLSearchParams(hash.slice(hash.indexOf('?') + 1));
    const group = (params.get('g') ?? '').trim();
    const cohortId = (params.get('c') ?? '').trim();
    if (group && cohortId) void watchViaDiscovery(group, cohortId);
  }
}

window.addEventListener('hashchange', route);

// ── operator ─────────────────────────────────────────────────────────────────
function renderIdentity(): void {
  const kp = loadKeypair();
  const el = $('op-identity');
  if (kp) {
    el.textContent = `operator key loaded (pubkey ${kp.pub.slice(0, 12)}…)`;
    const stored = localStorage.getItem(TOKEN_KEY);
    if (stored) ($('op-token') as HTMLInputElement).value = stored;
  } else {
    el.textContent = 'no operator key yet';
  }
}

$('op-init').addEventListener('click', async () => {
  let kp = loadKeypair();
  if (!kp) {
    kp = await generateKeypair();
    saveKeypair(kp);
  }
  renderIdentity();
});

$('op-register').addEventListener('click', async () => {
  const out = $('op-out');
  const kp = loadKeypair();
  if (!kp) {
    out.textContent = 'generate an operator key first';
    return;
  }
  const token = ($('op-token') as HTMLInputElement).value.trim();
  if (!token) {
    out.textContent = 'enter the device token';
    return;
  }
  const visibility = ($('op-visibility') as unknown as HTMLSelectElement).value;
  const groupRaw = ($('op-group') as HTMLInputElement).value.trim();
  const group_key = groupRaw && groupRaw.includes('-') ? groupRaw.split('-') : [];
  // Optional opaque cohort id (read off the operator's Locus cohort self-echo).
  // On register this only SEEDS it when currently NULL server-side; the
  // authoritative refresh is the signed live/start below.
  const cohort_id = ($('op-cohort') as HTMLInputElement).value.trim() || undefined;
  localStorage.setItem(TOKEN_KEY, token);
  try {
    const res = await fetch('/api/v1/devices', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ token, pubkey: kp.pub, visibility, group_key, cohort_id }),
    });
    out.textContent = `register → ${res.status} ${await res.text()}`;
  } catch (err) {
    out.textContent = `error: ${(err as Error).message}`;
  }
});

async function liveControl(action: 'live-start' | 'live-stop'): Promise<void> {
  const out = $('op-out');
  const kp = loadKeypair();
  if (!kp) {
    out.textContent = 'generate an operator key first';
    return;
  }
  const token = ($('op-token') as HTMLInputElement).value.trim();
  if (!token) {
    out.textContent = 'enter the device token';
    return;
  }
  const path = action === 'live-start' ? 'start' : 'stop';
  // For live-start, forward the operator's self-asserted opaque cohort id (read
  // off their Locus cohort self-echo). It is signature-covered AND echoed in the
  // body — the two must agree or the server's verify fails. Empty ⇒ null (the
  // device just isn't badge-discoverable until it asserts one). live-stop has no
  // cohort_id in its canon, so it's ignored there.
  const cohortId = action === 'live-start'
    ? (($('op-cohort') as HTMLInputElement).value.trim() || null)
    : null;
  try {
    const { headers, cohortId: signedCohortId } = await signLiveWrite(kp.priv, token, action, cohortId);
    const body: Record<string, unknown> = { token };
    if (action === 'live-start') body.cohort_id = signedCohortId;
    const res = await fetch(`/api/v1/live/${path}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
    out.textContent = `${path} → ${res.status} ${await res.text()}`;
  } catch (err) {
    out.textContent = `error: ${(err as Error).message}`;
  }
}

$('op-start').addEventListener('click', () => void liveControl('live-start'));
$('op-stop').addEventListener('click', () => void liveControl('live-stop'));

renderIdentity();
// Honour the hash present at first load (deep-link watch, or #/operator).
route();
